import { getSmallestLeadTime } from '@calculations/lead-time/smallest-lead-time';
import { createSelector } from '@reduxjs/toolkit';
import { selectAllIssues } from '@ui/features/quote-builder/state/selectors/issue';
import { selectPartInsightsByQuoteLineUuid } from '@ui/features/quote-builder/state/selectors/part-insights';
import { buildElementId } from '@ui/features/quote-builder/state/utilities/element-id';
import { RootState } from '@ui/store';

import { getIssues, getQuoteLineOrThrow } from '../utilities/selector-guards';

export const selectQuoteLine = (state: RootState, quoteLineUuid: string) =>
  getQuoteLineOrThrow(state.quoteBuilder, quoteLineUuid);

/**
 * Is this line known to exist in Postgres?
 *
 * Lines are created and deleted optimistically, so the store also holds lines
 * the database does not have: one that is still `creating` was not written
 * yet, and one whose deletion is in flight - or whose delete response never
 * made it back - may already be gone. A `syncing` line is only being refreshed
 * from Visual, so the row is there throughout.
 */
export function quoteLineIsPersisted(
  quoteLine: ReturnType<typeof selectQuoteLine>,
) {
  return quoteLine.status === 'ready' || quoteLine.status === 'syncing';
}

const selectQuoteLineLeadTimeIssues = createSelector(
  [selectAllIssues, (_: RootState, quoteLineUuid: string) => quoteLineUuid],
  (issues, quoteLineUuid) =>
    getIssues(issues, {
      area: 'quoteLine',
      field: 'leadTimeWeeks',
      quoteLineUuid,
    }),
);

const selectQuoteLineLeadTimeNotesIssues = createSelector(
  [selectAllIssues, (_: RootState, quoteLineUuid: string) => quoteLineUuid],
  (issues, quoteLineUuid) =>
    getIssues(issues, {
      area: 'quoteLine',
      field: 'leadTimeNotes',
      quoteLineUuid,
    }),
);

export const selectQuoteLineWithLeadTimeIssues = createSelector(
  [selectQuoteLine, selectQuoteLineLeadTimeIssues],
  (quoteLine, leadTimeIssues) => ({
    ...quoteLine,
    leadTimeIssues,
  }),
);

export const selectQuoteLineDetails = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).customerPartId,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).lastWin,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).leadTimeNotes,
    (_, quoteLineUuid: string) => quoteLineUuid,
    selectQuoteLineLeadTimeNotesIssues,
    selectPartInsightsByQuoteLineUuid,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partId,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).lastWin.hasLastWin,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).priceStrategy,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partSite,
  ],
  (
    customerPartId,
    lastWin,
    leadTimeNotes,
    quoteLineUuid,
    leadTimeNotesIssues,
    partInsights,
    partId,
    hasLastWin,
    priceStrategy,
    partSite,
  ) => {
    const leadTimeNotesElementId = buildElementId({
      uuid: quoteLineUuid,
      field: 'leadTimeNotes',
    });

    const customerPartIdElementId = buildElementId({
      uuid: quoteLineUuid,
      field: 'customerPartId',
    });

    return {
      customerPartId,
      customerPartIdElementId,
      lastWin,
      leadTimeNotes,
      leadTimeNotesIssues,
      leadTimeNotesElementId,
      partInsights,
      partId,
      hasLastWin,
      priceStrategy,
      partSite,
    };
  },
);

/** The single lead time that stands in for the line. See `getSmallestLeadTime`. */
export const selectQuoteLineSmallestPriceLeadTime = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).quotePrices,
  ],
  (quotePrices) => getSmallestLeadTime(Object.values(quotePrices)),
);

export const selectQuoteLineSpecs = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).specs,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partId,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).serviceChargeId,
  ],
  (specs, partId, serviceChargeId) => {
    return { specs, partId, serviceChargeId };
  },
);

export const selectUnreliableCostReasons = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).quotePrices,
  ],
  (quotePrices) => {
    const uniqueReasons = new Set(
      Object.values(quotePrices).flatMap(
        (price) => price.requiresUnsupportedCalculations,
      ),
    );
    return [...uniqueReasons].join(',');
  },
);
